import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableForum = ({ forum, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: forum._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '100%',
    marginBottom: '1rem',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div className="p-2 bg-base-200 border rounded mb-2 cursor-move flex items-center"
        {...attributes}
        {...listeners}>
        <strong className="mr-2">≡</strong> {forum.title}
      </div>
      <div>{children}</div>
    </div>
  );
};

const ForumManagement = () => {
  const [forums, setForums] = useState([]);
  const [groups, setGroups] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [parentForum, setParentForum] = useState('');
  const [visibleToGroup, setVisibleToGroup] = useState('');
  const [message, setMessage] = useState(null);
  const [editForumId, setEditForumId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editVisibleToGroup, setEditVisibleToGroup] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [forumToDelete, setForumToDelete] = useState(null);

  useEffect(() => {
    fetchForums();
    axiosInstance.get('/groups').then(res => setGroups(res.data));
  }, []);

  const fetchForums = () => {
    axiosInstance.get('/forums').then(res => setForums(res.data)).catch(() => setForums([]));
  };

  const handleAddForum = (e) => {
    e.preventDefault();
    axiosInstance.post('/forums', {
      title,
      description,
      parentForum: parentForum || null,
      visibleToGroups: visibleToGroup ? [visibleToGroup] : [],
    }).then(() => {
      setMessage({ type: 'success', text: 'Forum erfolgreich erstellt!' });
      setTitle(''); setDescription(''); setParentForum(''); setVisibleToGroup('');
      fetchForums();
    }).catch(() => setMessage({ type: 'error', text: 'Fehler beim Erstellen.' }));
  };

  const handleEdit = (forum) => {
    setEditForumId(forum._id);
    setEditTitle(forum.title);
    setEditDescription(forum.description);
    setEditVisibleToGroup(forum.visibleToGroups[0] || '');
  };

  const handleUpdateForum = (forumId) => {
    axiosInstance.put(`/forums/${forumId}`, {
      title: editTitle,
      description: editDescription,
      visibleToGroups: editVisibleToGroup ? [editVisibleToGroup] : [],
    }).then(() => {
      setMessage({ type: 'success', text: 'Forum aktualisiert!' });
      setEditForumId(null);
      fetchForums();
    }).catch(() => setMessage({ type: 'error', text: 'Fehler beim Aktualisieren.' }));
  };

  const confirmDelete = (forum) => {
    setForumToDelete(forum);
    setShowDeleteModal(true);
  };

  const handleDeleteForum = () => {
    axiosInstance.delete(`/forums/${forumToDelete._id}`)
      .then(() => {
        setMessage({ type: 'success', text: 'Forum gelöscht!' });
        fetchForums();
      }).catch(() => setMessage({ type: 'error', text: 'Fehler beim Löschen.' }))
      .finally(() => { setShowDeleteModal(false); setForumToDelete(null); });
  };

  const handleMainDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = forums.findIndex(f => f._id === active.id);
    const newIndex = forums.findIndex(f => f._id === over.id);
    const newForums = arrayMove(forums, oldIndex, newIndex);
    setForums(newForums);
    axiosInstance.post('/forums/reorder', { orderedIds: newForums.map(f => f._id) });
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g._id === groupId);
    return group ? group.name : 'Unbekannt';
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Foren-Verwaltung</h2>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          {message.text}
        </div>
      )}

      <DndContext collisionDetection={closestCenter} onDragEnd={handleMainDragEnd}>
        <SortableContext items={forums.map(f => f._id)} strategy={verticalListSortingStrategy}>
          {forums.map(forum => (
            <SortableForum key={forum._id} forum={forum}>
              <div className="card bg-base-100 shadow mb-4">
                <div className="card-body">
                  {editForumId === forum._id ? (
                    <>
                      <input className="input input-bordered mb-2" value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)} />
                      <textarea className="textarea textarea-bordered mb-2" value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)} />
                      <select className="select select-bordered mb-2"
                        value={editVisibleToGroup}
                        onChange={(e) => setEditVisibleToGroup(e.target.value)}>
                        <option value="">Keine Gruppe (öffentlich)</option>
                        {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <button className="btn btn-success btn-sm" onClick={() => handleUpdateForum(forum._id)}>💾 Speichern</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditForumId(null)}>❌ Abbrechen</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="font-bold">{forum.title}</h3>
                      <p>{forum.description}</p>
                      <div className="mb-2 text-sm text-gray-500">
                        Sichtbar: {forum.visibleToGroups.length === 0
                          ? <span className="badge badge-success">Öffentlich</span>
                          : forum.visibleToGroups.map(gid =>
                              <span key={gid} className="badge badge-info mr-1">
                                {getGroupName(gid)}
                              </span>
                            )}
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-outline btn-sm" onClick={() => handleEdit(forum)}>✏️ Bearbeiten</button>
                        <button className="btn btn-outline btn-error btn-sm" onClick={() => confirmDelete(forum)}>🗑️ Löschen</button>
                      </div>

                      {forum.subforums?.length > 0 && (
                        <div className="mt-4 ml-6">
                          {forum.subforums.map(sub => (
                            <div key={sub._id} className="p-2 bg-base-200 border rounded mb-2">
                              {editForumId === sub._id ? (
                                <>
                                  <input className="input input-bordered mb-2" value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)} />
                                  <textarea className="textarea textarea-bordered mb-2" value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)} />
                                  <select className="select select-bordered mb-2"
                                    value={editVisibleToGroup}
                                    onChange={(e) => setEditVisibleToGroup(e.target.value)}>
                                    <option value="">Keine Gruppe (öffentlich)</option>
                                    {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                                  </select>
                                  <div className="flex gap-2">
                                    <button className="btn btn-success btn-sm" onClick={() => handleUpdateForum(sub._id)}>💾 Speichern</button>
                                    <button className="btn btn-secondary btn-sm" onClick={() => setEditForumId(null)}>❌ Abbrechen</button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <strong>{sub.title}</strong><br />
                                  <span className="text-sm text-gray-500">{sub.description}</span>
                                  <div className="mb-2 text-sm text-gray-500">
                                    Sichtbar: {sub.visibleToGroups?.length === 0
                                      ? <span className="badge badge-success">Öffentlich</span>
                                      : sub.visibleToGroups.map(gid =>
                                          <span key={gid} className="badge badge-info mr-1">
                                            {getGroupName(gid)}
                                          </span>
                                        )}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1 mb-2">
                                    {sub.threadCount} Threads – {sub.postCount} Beiträge
                                    {sub.latestPost && (
                                      <div>
                                        Letzter Beitrag: <strong>{sub.latestPost.author}</strong> – {new Date(sub.latestPost.createdAt).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <button className="btn btn-outline btn-sm" onClick={() => handleEdit(sub)}>✏️ Bearbeiten</button>
                                    <button className="btn btn-outline btn-error btn-sm" onClick={() => confirmDelete(sub)}>🗑️ Löschen</button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </SortableForum>
          ))}
        </SortableContext>
      </DndContext>

      <form onSubmit={handleAddForum} className="mt-6 card bg-base-100 shadow p-4">
        <h4 className="font-bold mb-2">Neues Forum/Subforum hinzufügen</h4>
        <input className="input input-bordered mb-2" type="text" placeholder="Titel"
          value={title} onChange={(e) => setTitle(e.target.value)} required />
        <textarea className="textarea textarea-bordered mb-2" placeholder="Beschreibung"
          value={description} onChange={(e) => setDescription(e.target.value)} />
        <select className="select select-bordered mb-2" value={parentForum}
          onChange={(e) => setParentForum(e.target.value)}>
          <option value="">Hauptforum</option>
          {forums.map(f => <option key={f._id} value={f._id}>{f.title}</option>)}
        </select>
        <select className="select select-bordered mb-2"
          value={visibleToGroup}
          onChange={(e) => setVisibleToGroup(e.target.value)}>
          <option value="">Keine Gruppe (öffentlich)</option>
          {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
        </select>
        <button className="btn btn-primary w-full">Forum erstellen</button>
      </form>

      {showDeleteModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Forum löschen</h3>
            <p className="py-4">Bist du sicher, dass du <strong>{forumToDelete?.title}</strong> löschen möchtest?</p>
            <div className="modal-action">
              <button className="btn" onClick={() => setShowDeleteModal(false)}>Abbrechen</button>
              <button className="btn btn-error" onClick={handleDeleteForum}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumManagement;
